import {
  ENROLLMENT_STATUS_LABELS,
  type EnrollmentStatus,
  PLANS,
  type PlanCode,
} from "@byfluent/domain";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { Alert, Badge, Button, Empty, Loading, PageHeader } from "../../components/ui";
import { api, errorMessage, qs } from "../../lib/api";
import { dateBR, formatBrPhone } from "../../lib/format";

interface StudentRow {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  enrollmentId: string;
  status: EnrollmentStatus;
  planCode: PlanCode;
  teacherName: string | null;
  createdAt: string;
}

export const ENROLLMENT_COLOR: Record<
  EnrollmentStatus,
  "green" | "gold" | "red" | "gray" | "blue" | "violet"
> = {
  link_sent: "blue",
  filled: "violet",
  active: "green",
  overdue: "gold",
  suspended: "red",
  cancelled: "gray",
  expired: "gray",
};

export function AdminStudents() {
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ["admin-students", search],
    queryFn: () =>
      api.get<{ items: StudentRow[] }>(
        `/v1/admin/students${qs({ q: search || undefined, limit: 50 })}`,
      ),
  });
  return (
    <>
      <PageHeader
        title="Alunos"
        subtitle="Busque pelo nome, e-mail ou telefone para abrir a visão completa e as ações de suporte."
      />
      <form
        className="row"
        style={{ marginBottom: 16 }}
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(q.trim());
        }}
      >
        <input
          className="input"
          style={{ flex: 1, minWidth: 220 }}
          placeholder="Nome, e-mail ou telefone"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Buscar aluno"
        />
        <Button type="submit" variant="secondary">
          Buscar
        </Button>
      </form>
      {list.isLoading ? <Loading /> : null}
      {list.error ? <Alert kind="risk">{errorMessage(list.error)}</Alert> : null}
      {list.data?.items.length === 0 ? <Empty>Nenhum aluno encontrado.</Empty> : null}
      {list.data?.items.length ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Aluno</th>
                <th>Plano</th>
                <th>Professor</th>
                <th>Matrícula</th>
                <th>Desde</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((s) => (
                <tr
                  key={s.id}
                  className="clickable"
                  onClick={() => navigate(`/admin/alunos/${s.id}`)}
                >
                  <td>
                    <strong>{s.name}</strong>
                    <div className="small muted">
                      {s.email} {s.phone ? `· ${formatBrPhone(s.phone)}` : ""}
                    </div>
                  </td>
                  <td>{PLANS[s.planCode]?.name}</td>
                  <td>{s.teacherName}</td>
                  <td>
                    <Badge color={ENROLLMENT_COLOR[s.status]}>
                      {ENROLLMENT_STATUS_LABELS[s.status]}
                    </Badge>
                  </td>
                  <td className="small">{dateBR(s.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
